import { useUserStore } from '../../../store/useUserStore';
import { useState, useCallback, useEffect } from 'react';
// import Url from '../../data/url';
import { useNavigate, useLocation } from 'react-router-dom';
import { Report } from '../../../types';
import { BtnCss } from '../../../utility/css';
import { TextField } from '@mui/material';
import { useKidDetailQuery, useCreateQnaMutation } from '../../../queries';
import {
  SELECT_STYLE,
  COMMUNITY_TYPE,
  COMMUNITY_QNA_TYPE,
  COMMUNITY_USER_TYPE,
} from '../../../data/define';
import toast from 'react-hot-toast';
import moment from 'moment';
import CustomSwitch from '../../common/CustomSwitch';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FullHeightContainer from '../../common/FullHeightContainer';
import TopHeaderBar from './TopHeaderBar';
import BottomFixedBtn from '../../common/BottomFixedBtn';

const ComparisonType = {
  Latest: 'latest',
  Cumulative: 'cumulative',
};

interface GrowthProps {
  type: number;
  setWriteModalOpen: (open: boolean) => void;
  onClose?: () => void;
}

export default function Growth({ type, setWriteModalOpen, onClose }: GrowthProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUserStore();
  const [comparisonType, setComparisonType] = useState(ComparisonType.Cumulative);
  const [registorDisabled, setRegistorDisabled] = useState(false);
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);
  const [lastReportId, setLastReportId] = useState<number | null>(null);
  const [isHidden, setIsHidden] = useState(false);
  const [content, setContent] = useState('');

  const { data: kidDetail } = useKidDetailQuery(user?.mainKid?.id as number, {
    enabled: !!user?.mainKid?.id,
  });

  useEffect(() => {
    if (kidDetail) {
      if (kidDetail.reports.length < 1) {
        toast.error('리포트가 없습니다. 리포트를 생성해주세요.');
        return;
      }
      // 리포트를 날짜순으로 정렬 (최신순)
      const sortedReports = [...(kidDetail.reports || [])].sort(
        (a: Report, b: Report) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setReports(sortedReports);
      // 가장 최근 리포트를 선택 (이미 createdAt 기준 내림차순 정렬됨)
      const latestReport = sortedReports[0] || null;
      setSelectedReportId(latestReport?.id || null);
      // 두 번째 리포트가 있으면 이전 리포트로 설정, 없으면 null
      setLastReportId(sortedReports.length >= 2 ? sortedReports[1]?.id : null);
    }
  }, [kidDetail]);

  // selectedReportId가 변경될 때마다 이전 리포트 목록 업데이트
  useEffect(() => {
    if (!selectedReportId || reports.length === 0) {
      setLastReportId(null);
      return;
    }
    // 선택한 리포트 찾기
    const selectedReport = reports.find((report) => report.id === selectedReportId);
    if (!selectedReport) {
      setLastReportId(null);
      return;
    }
    // 선택한 리포트보다 과거의 리포트만 필터링
    const previousReports = reports.filter(
      (report) =>
        new Date(report.createdAt).getTime() < new Date(selectedReport.createdAt).getTime()
    );
    // 이전 리포트가 있으면 첫 번째(가장 최신)를 선택, 없으면 null
    if (previousReports.length > 0) {
      // 현재 lastReportId가 유효한 이전 리포트 목록에 포함되어 있으면 유지, 아니면 첫 번째로 설정
      const isValidLastReport = previousReports.some((report) => report.id === lastReportId);
      if (!isValidLastReport) {
        setLastReportId(previousReports[0]?.id || null);
      }
    } else {
      setLastReportId(null);
    }
  }, [selectedReportId, reports]);

  const { mutate: createQna } = useCreateQnaMutation();

  const handleCreateQna = useCallback(() => {
    const apiParams: any = {
      content,
      questionType: COMMUNITY_QNA_TYPE.GROWTH,
      userType: COMMUNITY_USER_TYPE.USER, // Growth QNA의 경우 값 고정
      kidId: user?.mainKid?.id,
      isPrivate: isHidden,
      reportId: selectedReportId,
    };
    if (comparisonType === ComparisonType.Latest) {
      apiParams.compareReportId = lastReportId;
    }
    createQna(apiParams, {
      onSuccess: () => {
        if (onClose) {
          onClose();
        } else {
          navigate(-1);
        }
      },
      onError: (error) => {
        console.error('Error creating QnA:', error);
        toast.error('Failed to create QnA');
      },
    });
  }, [
    content,
    user,
    lastReportId,
    comparisonType,
    selectedReportId,
    isHidden,
    createQna,
    onClose,
    navigate,
  ]);

  useEffect(() => {
    setRegistorDisabled(content.length < 1);
  }, [content]);

  return (
    <FullHeightContainer>
      <TopHeaderBar
        type={type}
        setWriteModalOpen={setWriteModalOpen}
        registorDisabled={registorDisabled}
        onSubmit={handleCreateQna}
        onClose={onClose}
      />
      <div className="pageInner">
        <div className="w-full text-[#1C1C1C] font-[700] text-[15px] text-left mt-[20px]">
          리포트
        </div>
        <div className="w-full flex items-center justify-between gap-2 mt-[5px]">
          <div className="flex items-center justify-start">
            <Select
              variant="outlined"
              value={selectedReportId || ''}
              onChange={(e) => {
                const value = Number(e.target.value);
                setSelectedReportId(value);
                navigate(
                  { pathname: location.pathname, search: `?reportId=${value}` },
                  { replace: true }
                );
              }}
              renderValue={(selected) => (
                <>
                  {comparisonType === ComparisonType.Cumulative && (
                    <span style={{ color: '#969696', fontWeight: 600 }}>조회일&nbsp;</span>
                  )}
                  <span style={{ color: '#363636', fontWeight: 700 }}>
                    {reports.length > 0 ? (
                      <>
                        {moment(reports.find((report) => report.id === selected)?.createdAt).format(
                          'YY.MM.DD'
                        ) || '선택된 리포트 없음'}
                      </>
                    ) : (
                      <>...</>
                    )}
                  </span>
                </>
              )}
              IconComponent={KeyboardArrowDownIcon}
              sx={{ ...SELECT_STYLE, minWidth: '120px', backgroundColor: '#F0FCFC' }}
            >
              {reports?.map((report) => (
                <MenuItem key={report.id} value={report.id} sx={{ fontSize: '14px' }}>
                  <span className="text-[14px] text-[#363636]">
                    {moment(report.createdAt).format('YYYY.MM.DD')}
                  </span>
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              className="px-3 py-2"
              style={comparisonType === ComparisonType.Latest ? BtnCss.Focused : BtnCss.Blured}
              onClick={() => setComparisonType(ComparisonType.Latest)}
            >
              이전
            </button>
            <button
              type="button"
              className="px-3 py-2"
              style={comparisonType === ComparisonType.Cumulative ? BtnCss.Focused : BtnCss.Blured}
              onClick={() => setComparisonType(ComparisonType.Cumulative)}
            >
              누적
            </button>
          </div>
        </div>
        {comparisonType === ComparisonType.Latest && (
          <div className="w-full">
            <div className="w-full text-[#1C1C1C] font-[700] text-[15px] text-left mt-[20px]">
              이전 리포트
            </div>
            <div className="w-full flex justify-start mt-[5px]">
              <Select
                variant="outlined"
                value={lastReportId || ''}
                onChange={(e) => setLastReportId(Number(e.target.value))}
                renderValue={(selected) => {
                  if (!selected) {
                    return <span style={{ color: '#969696' }}>-</span>;
                  }
                  const selectedReport = reports.find((report) => report.id === selected);
                  return (
                    <span style={{ color: '#363636', fontWeight: 700 }}>
                      {selectedReport ? moment(selectedReport.createdAt).format('YY.MM.DD') : '-'}
                    </span>
                  );
                }}
                IconComponent={KeyboardArrowDownIcon}
                sx={{
                  ...SELECT_STYLE,
                  minWidth: '120px',
                  backgroundColor: '#FFEFF6',
                  fontWeight: 600,
                }}
              >
                {(() => {
                  // 선택한 리포트보다 과거의 리포트만 필터링
                  const selectedReport = reports.find((report) => report.id === selectedReportId);
                  if (!selectedReport) return null;

                  const previousReports = reports.filter(
                    (report) =>
                      new Date(report.createdAt).getTime() <
                      new Date(selectedReport.createdAt).getTime()
                  );

                  if (previousReports.length === 0) {
                    return (
                      <MenuItem value="" disabled sx={{ fontSize: '14px' }}>
                        <span className="text-[14px] text-[#969696]">-</span>
                      </MenuItem>
                    );
                  }

                  return previousReports.map((report) => (
                    <MenuItem key={report.id} value={report.id} sx={{ fontSize: '14px' }}>
                      <span className="text-[14px] text-[#363636]">
                        {moment(report.createdAt).format('YY.MM.DD')}
                      </span>
                    </MenuItem>
                  ));
                })()}
              </Select>
            </div>
          </div>
        )}
        <div className="w-full mt-[30px]">
          <div className="flex justify-between mb-2 items-center gap-2">
            <div className="font-[700] text-[16px] text-[#1C1C1C] text-left">내용</div>
            <div className="flex items-center justify-center gap-2">
              <CustomSwitch
                value={isHidden}
                label="비공개"
                onToggle={() => setIsHidden(!isHidden)}
              />
            </div>
          </div>
          <div className="w-full relative mb-[12px]">
            <TextField
              placeholder="내용을 작성해 주세요"
              variant="outlined"
              fullWidth
              multiline
              rows={10}
              value={content}
              onChange={(e) => {
                if (e.target.value.length < 301) {
                  setContent(e.target.value);
                }
              }}
              sx={{ mb: 2 }}
            />
            <div className="absolute right-2 bottom-5 text-[12px] text-[#767676]">
              {content.length}/300
            </div>
          </div>
        </div>
      </div>
      <BottomFixedBtn
        title={type === COMMUNITY_TYPE.FREE_BOARD ? '작성 완료' : '등록'}
        onClick={handleCreateQna}
        disabled={registorDisabled}
      />
    </FullHeightContainer>
  );
}
